import { useState } from "react";
import { toast } from "sonner";
import { BadgePercent, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const RequestDialog = ({ open, onOpenChange, offer }) => {
  const [f, setF] = useState({ project_name: "", address: "", description: "" });
  const [busy, setBusy] = useState(false);
  const field = "bg-white border-slate-300 text-slate-900";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/portal/study-requests", f); toast.success("Request sent! Our team will be in touch shortly."); onOpenChange(false); setF({ project_name: "", address: "", description: "" }); }
    catch (err) { toast.error(errMsg(err, "Could not send request")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-slate-900 border-slate-200" data-testid="study-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Request a new study</DialogTitle>
          <DialogDescription className="text-slate-500">{offer ? `Your ${offer.percent}% repeat-client discount is applied automatically to the invoice.` : "Tell us about your next project."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="study-request-name" required minLength={2} placeholder="Project name" value={f.project_name} onChange={(e) => setF({ ...f, project_name: e.target.value })} className={field} />
          <Input data-testid="study-request-address" placeholder="Property address (optional)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={field} />
          <Textarea data-testid="study-request-description" required minLength={10} rows={4} placeholder="What do you plan to build or operate? Any timeline or budget?" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={field} />
          <Button data-testid="study-request-submit" disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send request</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const RepeatOffer = ({ offer, dark }) => {
  const [open, setOpen] = useState(false);
  if (!offer) return null;
  return (
    <div data-testid="repeat-offer" className={`rounded-xl p-5 flex flex-wrap items-center gap-4 ${dark ? "border border-amber-400/40 bg-amber-400/10" : "border border-amber-200 bg-amber-50"}`}>
      <BadgePercent className={`h-8 w-8 shrink-0 ${dark ? "text-amber-300" : "text-amber-600"}`} />
      <div className="flex-1 min-w-[200px]">
        <p className="font-display font-semibold" data-testid="repeat-offer-title">Thank you! Enjoy {offer.percent}% off your next study</p>
        <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>Valid until {fmt(offer.expires_at)}. Applied automatically, no code needed.</p>
      </div>
      <Button data-testid="repeat-offer-start" onClick={() => setOpen(true)} className="rounded-full bg-amber-400 text-amber-950 hover:bg-amber-300">Start your next study<ArrowRight className="h-4 w-4 ml-2" /></Button>
      <RequestDialog open={open} onOpenChange={setOpen} offer={offer} />
    </div>
  );
};
